import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  MessageCircle,
  Search,
  Send,
  Circle,
  CheckCircle2,
  User,
  Clock,
  X,
  Bell,
  BellOff,
  ChevronRight,
  Phone,
  Mail
} from 'lucide-react';
import apiService from '../services/api';
import { getAssistanceCommercantId } from '../utils/assistanceSession';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { API_BASE_URL, getWebSocketBaseUrl } from '../config/api.config';
import { getApiOrigin } from '../utils/apiOrigin';

const getWebSocketUrl = () => getWebSocketBaseUrl();

// Construire l'URL complète de l'avatar
const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  
  // Si c'est déjà une URL complète (http:// ou https://), la retourner telle quelle
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
    return avatar;
  }
  
  // Si c'est un chemin relatif (/uploads/...), construire l'URL complète
  if (avatar.startsWith('/uploads/')) {
    const baseUrl = getApiOrigin(API_BASE_URL);
    return `${baseUrl}${avatar}`;
  }
  
  // Sinon, retourner null (avatar invalide)
  return null;
};
const Chat = () => {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [commercantId, setCommercantId] = useState(null);
  const [chatActif, setChatActif] = useState(false);
  const [chatDisponible, setChatDisponible] = useState(false);
  const [statutLoading, setStatutLoading] = useState(false);
  const [avatarErrors, setAvatarErrors] = useState(new Set());
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const socketRef = useRef(null);

  // Connexion Socket.IO pour les mises à jour en temps réel
  useEffect(() => {
    if (!chatActif || !commercantId) {
      return;
    }

    // Récupérer l'ID utilisateur depuis localStorage
    const getCurrentUserId = () => {
      try {
        const userData = localStorage.getItem('admin_user');
        if (userData) {
          const user = JSON.parse(userData);
          return user?.id || null;
        }
      } catch (error) {
        console.error('Erreur parsing user data:', error);
      }
      return null;
    };

    const userId = getCurrentUserId();
    if (!userId) {
      return;
    }

    // Créer la connexion WebSocket
    const socket = io(getWebSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Gérer les événements de connexion
    socket.on('connect', () => {
      console.log('[Chat WebSocket Admin] Connecté');
      
      // Authentifier l'utilisateur
      socket.emit('chat:authenticate', {
        userId: userId,
        token: null
      });

      // Rejoindre la salle du commerçant
      socket.emit('chat:join_conversation', {
        commercantId: commercantId
      });
    });

    // Écouter les nouveaux messages
    socket.on('chat:new_message', (messageData) => {
      console.log('[Chat WebSocket Admin] Nouveau message reçu:', messageData);
      
      // Ignorer les messages qu'on a envoyés nous-mêmes (ils sont déjà gérés par handleSendMessage)
      // Si le message vient du commerçant (isFromClient === false) et expediteurId === userId, on l'ignore
      if (!messageData.isFromClient && messageData.expediteurId === userId) {
        console.log('[Chat WebSocket Admin] Message ignoré (envoyé par nous-même, déjà ajouté via réponse API)');
        // Mais on met quand même à jour les conversations pour le dernier message
        if (messageData.commercantId === commercantId) {
          apiService.getChatConversations(commercantId)
            .then(response => {
              const data = response?.data || response;
              setConversations(data.conversations || []);
            })
            .catch(err => console.error('Erreur rechargement conversations:', err));
        }
        return;
      }
      
      // Si une conversation est sélectionnée et que le message lui appartient
      if (selectedConversation && messageData.commercantId === commercantId) {
        const messageClientId = messageData.clientId || (messageData.isFromClient ? messageData.expediteurId : messageData.destinataireId);
        
        if (messageClientId === selectedConversation.clientId) {
          // Ajouter le message s'il n'existe pas déjà
          setMessages(prev => {
            const messageExists = prev.some(msg => msg.id === messageData.id);
            if (messageExists) {
              console.log('[Chat WebSocket Admin] Message ignoré (déjà présent)');
              return prev;
            }
            
            return [...prev, {
              ...messageData,
              dateCreation: messageData.dateCreation || messageData.createdAt,
              isFromClient: messageData.isFromClient !== undefined ? messageData.isFromClient : false
            }];
          });

          // Scroll vers le bas après le rendu
          const scrollToBottom = () => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          };
          setTimeout(scrollToBottom, 50);
          setTimeout(scrollToBottom, 150);
          setTimeout(scrollToBottom, 300);
        }
      }

      // Mettre à jour les conversations pour afficher le dernier message
      if (messageData.commercantId === commercantId) {
        apiService.getChatConversations(commercantId)
          .then(response => {
            const data = response?.data || response;
            setConversations(data.conversations || []);
          })
          .catch(err => console.error('Erreur rechargement conversations:', err));
      }
    });

    socket.on('chat:authenticated', () => {
      console.log('[Chat WebSocket Admin] Authentifié avec succès');
    });

    socket.on('chat:auth_error', (error) => {
      console.error('[Chat WebSocket Admin] Erreur authentification:', error);
    });

    socket.on('chat:error', (error) => {
      console.error('[Chat WebSocket Admin] Erreur:', error);
    });

    socket.on('disconnect', () => {
      console.log('[Chat WebSocket Admin] Déconnecté');
    });

    socket.on('connect_error', (err) => {
      console.error('[Chat WebSocket Admin] Erreur connexion:', err);
    });

    // Rejoindre la conversation sélectionnée quand elle change
    if (selectedConversation) {
      socket.emit('chat:join_conversation', {
        commercantId: commercantId,
        clientId: selectedConversation.clientId
      });
    }

    // Nettoyage lors du démontage
    return () => {
      if (socket.connected) {
        socket.disconnect();
      }
    };
  }, [chatActif, commercantId, selectedConversation]);

  // Récupérer le commercantId de l'utilisateur actuel.
  // En assistance, la session existante porte déjà le commerçant impersonné.
  useEffect(() => {
    const assistanceCommercantId = getAssistanceCommercantId();
    if (assistanceCommercantId) {
      setCommercantId(assistanceCommercantId);
      return;
    }

    const getCurrentUser = () => {
      try {
        const userData = localStorage.getItem('admin_user');
        if (userData) {
          const user = JSON.parse(userData);
          const role = user?.role || null;
          const commercantId = user?.commercantId || user?.commercant?.id || null;
          
          // Normaliser le rôle
          const normalizedRole = role === 'commercant' || role === 'COMMERCANT' ? 'COMMERCANT' : role;
          
          // Vérifier que c'est bien un commerçant
          if (normalizedRole !== 'COMMERCANT') {
            console.error('[Chat] Accès refusé : utilisateur n\'est pas un commerçant', { role, normalizedRole });
            return { commercantId: null, role: normalizedRole, error: 'NOT_COMMERCANT' };
          }
          
          return { commercantId, role, error: null };
        }
      } catch (error) {
        console.error('Erreur parsing user data:', error);
        return { commercantId: null, role: null, error: 'PARSE_ERROR' };
      }
      return { commercantId: null, role: null, error: 'NO_USER_DATA' };
    };

    const userInfo = getCurrentUser();
    
    if (userInfo.error === 'NOT_COMMERCANT') {
      toast.error('Le chat est réservé aux commerçants uniquement.');
      // Rediriger vers la page appropriée selon le rôle
      const redirectPath = userInfo.role === 'SUPER_ADMIN' || userInfo.role === 'super-admin' 
        ? '/dashboard' 
        : '/commercants';
      navigate(redirectPath);
      return;
    }
    
    if (!userInfo.commercantId) {
      toast.error('Commerçant non trouvé. Veuillez vous reconnecter.');
      const redirectPath = userInfo.role === 'SUPER_ADMIN' || userInfo.role === 'super-admin' 
        ? '/dashboard' 
        : '/commercants';
      navigate(redirectPath);
      return;
    }

    setCommercantId(userInfo.commercantId);
  }, [navigate, toast]);

  // Charger le statut du chat
  useEffect(() => {
    const loadChatStatut = async () => {
      if (!commercantId) return;

      try {
        const response = await apiService.getChatStatut(commercantId);
        const statut = response?.data || response;
        setChatActif(statut.chatActif || false);
        setChatDisponible(statut.chatDisponible || false);
      } catch (err) {
        console.error('Erreur chargement statut chat:', err);
      }
    };

    loadChatStatut();
  }, [commercantId]);

  // Charger les conversations
  useEffect(() => {
    const loadConversations = async () => {
      if (!commercantId) return;

      try {
        setLoading(true);
        const response = await apiService.getChatConversations(commercantId);
        const data = response?.data || response;
        setConversations(data.conversations || []);
      } catch (err) {
        console.error('Erreur chargement conversations:', err);
        toast.error('Erreur lors du chargement des conversations');
      } finally {
        setLoading(false);
      }
    };

    if (chatActif && commercantId) {
      loadConversations();
      // Rafraîchir toutes les 60 secondes (Socket.IO met à jour en temps réel)
      const interval = setInterval(loadConversations, 60000);
      return () => clearInterval(interval);
    }
  }, [commercantId, chatActif]);

  // Charger les messages d'une conversation
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConversation || !commercantId) return;

      try {
        setLoadingMessages(true);
        const response = await apiService.getChatMessages(commercantId, selectedConversation.clientId);
        const data = response?.data || response;
        const messagesList = Array.isArray(data?.messages) ? data.messages : (Array.isArray(data) ? data : []);
        const filteredMessages = messagesList.filter(msg => msg && msg.id);
        
        // Trier les messages par date (du plus ancien au plus récent) pour affichage correct
        const sortedMessages = [...filteredMessages].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.dateCreation || 0);
          const dateB = new Date(b.createdAt || b.dateCreation || 0);
          return dateA - dateB; // Ordre croissant (plus ancien en premier)
        });
        
        setMessages(sortedMessages);
        setLoadingMessages(false);
        window.dispatchEvent(new CustomEvent('clickorico:refresh-chat-unread-count'));
        
        // Le scroll sera géré par le useEffect qui surveille les changements de messages
      } catch (err) {
        console.error('Erreur chargement messages:', err);
        toast.error('Erreur lors du chargement des messages');
      } finally {
        setLoadingMessages(false);
      }
    };

    loadMessages();

    // Plus besoin de rafraîchir toutes les 5 secondes, Socket.IO le fait en temps réel
    // const interval = setInterval(loadMessages, 5000);
    // return () => clearInterval(interval);
  }, [selectedConversation, commercantId]);

  // Scroll vers le bas quand les messages changent ou quand une conversation est sélectionnée
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current && !loadingMessages) {
      // Fonction pour scroller vers le bas - méthode très agressive
      const scrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (!container) return;
        
        // Calculer la position de scroll maximale
        const maxScroll = container.scrollHeight - container.clientHeight;
        
        // Forcer le scroll vers le bas
        container.scrollTop = maxScroll;
        
        // Vérifier et corriger si nécessaire (avec une marge de 10px)
        if (container.scrollTop < maxScroll - 10) {
          container.scrollTop = container.scrollHeight;
        }
      };
      
      // Attendre que le DOM soit complètement mis à jour
      // Utiliser plusieurs tentatives avec des délais croissants
      const attemptScroll = () => {
        scrollToBottom();
      };
      
      // Double requestAnimationFrame pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          attemptScroll();
          // Essayer plusieurs fois avec des délais
          setTimeout(attemptScroll, 0);
          setTimeout(attemptScroll, 50);
          setTimeout(attemptScroll, 100);
          setTimeout(attemptScroll, 200);
          setTimeout(attemptScroll, 400);
          setTimeout(attemptScroll, 600);
          setTimeout(attemptScroll, 800);
        });
      });
    }
  }, [messages, selectedConversation, loadingMessages]);

  const handleToggleDisponibilite = async () => {
    if (!commercantId) return;

    try {
      setStatutLoading(true);
      const newDisponible = !chatDisponible;
      await apiService.updateChatDisponibilite(commercantId, newDisponible);
      setChatDisponible(newDisponible);
      toast.success(newDisponible 
        ? 'Vous êtes maintenant disponible pour le chat' 
        : 'Vous êtes maintenant hors ligne');
    } catch (err) {
      console.error('Erreur toggle disponibilité:', err);
      toast.error('Erreur lors de la mise à jour de la disponibilité');
    } finally {
      setStatutLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !commercantId || sending) return;

    try {
      setSending(true);
      const response = await apiService.sendChatMessage(
        commercantId,
        selectedConversation.clientId,
        newMessage.trim()
      );
      
      // Ajouter le message à la liste localement
      const data = response?.data || response;
      const newMessageData = data.message || data;
      
      // Ajouter le message uniquement s'il n'existe pas déjà (au cas où Socket.IO l'aurait ajouté entre temps)
      setMessages(prev => {
        const messageExists = prev.some(msg => msg.id === newMessageData.id);
        if (!messageExists) {
          return [...prev, newMessageData];
        }
        return prev;
      });
      setNewMessage('');
      
      // Scroll vers le bas après le rendu
      const scrollToBottom = () => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      };
      setTimeout(scrollToBottom, 50);
      setTimeout(scrollToBottom, 150);
      setTimeout(scrollToBottom, 300);

      // Les conversations seront mises à jour automatiquement via Socket.IO
      // Mais on peut aussi les recharger immédiatement pour une meilleure UX
      try {
        const conversationsResponse = await apiService.getChatConversations(commercantId);
        const conversationsData = conversationsResponse?.data || conversationsResponse;
        setConversations(conversationsData.conversations || []);
      } catch (err) {
        console.error('Erreur rechargement conversations:', err);
      }
    } catch (err) {
      console.error('Erreur envoi message:', err);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.clientNom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatMessageDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now.getTime() - 86400000).toDateString() === date.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (isYesterday) {
      return `Hier à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  // Si le chat n'est pas activé, afficher un message avec toggle
  if (commercantId && !chatActif) {
    return (
      <div className="space-y-6">
        {toasts.map((toastItem) => (
          <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
        ))}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chat avec les clients</h1>
            <p className="text-gray-600">Système de chat en direct</p>
          </div>
        </div>
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Chat non activé</h3>
              <p className="text-sm text-gray-600">
                Activez le chat pour permettre à vos clients de vous contacter en direct.
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  await apiService.updateCommercantChat(commercantId, true);
                  setChatActif(true);
                  toast.success('Chat activé avec succès !');
                } catch (err) {
                  console.error('Erreur activation chat:', err);
                  toast.error('Erreur lors de l\'activation du chat');
                }
              }}
              className="btn-primary"
            >
              Activer le chat
            </button>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <MessageCircle className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-700 text-center">
              Une fois activé, vous pourrez gérer toutes vos conversations depuis cette page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Chat avec les clients</h1>
          <p className="text-gray-600">Répondez aux questions de vos clients en temps réel</p>
        </div>
        
        {/* Toggle disponibilité */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Circle className={`h-3 w-3 ${chatDisponible ? 'text-green-500 fill-current' : 'text-gray-400'}`} />
            <span className="text-sm text-gray-600">
              {chatDisponible ? 'Disponible' : 'Hors ligne'}
            </span>
          </div>
          <button
            onClick={handleToggleDisponibilite}
            disabled={statutLoading}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              chatDisponible
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${statutLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {chatDisponible ? (
              <>
                <Bell className="h-4 w-4" />
                <span>Se mettre hors ligne</span>
              </>
            ) : (
              <>
                <BellOff className="h-4 w-4" />
                <span>Se mettre en ligne</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="flex-1 flex bg-white rounded-lg shadow-sm border overflow-hidden" style={{ height: 'calc(100vh - 250px)' }}>
        {/* Liste des conversations */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col">
          {/* Recherche */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une conversation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Liste */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm">
                  {searchTerm ? 'Aucune conversation trouvée' : 'Aucune conversation pour le moment'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredConversations.map((conv) => (
                  <button
                    key={`${conv.commercantId}_${conv.clientId}`}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedConversation?.clientId === conv.clientId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {getAvatarUrl(conv.clientAvatar) && !avatarErrors.has(`${conv.commercantId}_${conv.clientId}`) ? (
                          <img
                            src={getAvatarUrl(conv.clientAvatar)}
                            alt={conv.clientNom}
                            className="h-12 w-12 rounded-full object-cover"
                            onError={() => {
                              setAvatarErrors(prev => new Set([...prev, `${conv.commercantId}_${conv.clientId}`]));
                            }}
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {conv.clientNom || 'Client'}
                          </p>
                          {conv.unreadCount > 0 && (
                            <span className="ml-2 flex-shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-semibold">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-1">
                          {conv.lastMessage || 'Aucun message'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(conv.lastMessageDate)}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Zone de conversation */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* En-tête de la conversation */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getAvatarUrl(selectedConversation.clientAvatar) && !avatarErrors.has(`${selectedConversation.commercantId}_${selectedConversation.clientId}`) ? (
                      <img
                        src={getAvatarUrl(selectedConversation.clientAvatar)}
                        alt={selectedConversation.clientNom}
                        className="h-10 w-10 rounded-full object-cover"
                        onError={() => {
                          setAvatarErrors(prev => new Set([...prev, `${selectedConversation.commercantId}_${selectedConversation.clientId}`]));
                        }}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedConversation.clientNom || 'Client'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedConversation.clientEmail || ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div 
                ref={messagesContainerRef} 
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
              >
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm">Aucun message pour le moment</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.filter(msg => msg && msg.id).map((msg, index) => {
                      if (!msg || !msg.id) return null;
                      
                      const isFromClient = msg.isFromClient || false;
                      const prevMsg = index > 0 ? messages[index - 1] : null;
                      const showDate = index === 0 || 
                        (prevMsg && msg.createdAt && prevMsg.createdAt &&
                        new Date(msg.createdAt) - new Date(prevMsg.createdAt) > 300000); // 5 minutes

                      const prevMsgFromSameSender = index > 0 && 
                        messages[index - 1] && 
                        (messages[index - 1].isFromClient === isFromClient);
                      
                      return (
                        <div key={msg.id}>
                          {showDate && msg.createdAt && (
                            <div className="flex justify-center my-4">
                              <span className="text-xs text-gray-400 bg-gray-200 px-3 py-1 rounded-full">
                                {formatMessageDate(msg.createdAt).includes('Hier') 
                                  ? formatMessageDate(msg.createdAt)
                                  : new Date(msg.createdAt).toLocaleDateString('fr-FR', { 
                                      weekday: 'long', 
                                      day: 'numeric', 
                                      month: 'long' 
                                    })
                                }
                              </span>
                            </div>
                          )}
                          {/* Séparateur marqué entre messages de différents expéditeurs */}
                          {index > 0 && !prevMsgFromSameSender && !showDate && (
                            <div className="my-3 border-t border-gray-300"></div>
                          )}
                          <div className={`flex ${isFromClient ? 'justify-start' : 'justify-end'} mb-2`}>
                            <div className={`max-w-[70%] rounded-lg px-4 py-2 ${
                              isFromClient
                                ? 'bg-white border border-gray-200'
                                : 'bg-blue-600 text-white'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap break-words">
                                {msg.contenu || ''}
                              </p>
                              {msg.createdAt && (
                                <div className={`flex items-center space-x-1 mt-1 ${
                                  isFromClient ? 'text-gray-400' : 'text-blue-200'
                                }`}>
                                  <span className="text-xs">
                                    {formatMessageDate(msg.createdAt)}
                                  </span>
                                  {!isFromClient && (
                                    <CheckCircle2 className={`h-3 w-3 ${msg.lu ? 'text-blue-200' : 'text-gray-300'}`} />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div 
                      ref={messagesEndRef} 
                      style={{ 
                        minHeight: '1px',
                        width: '100%',
                        flexShrink: 0
                      }} 
                    />
                  </>
                )}
              </div>

              {/* Input de message */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <textarea
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Tapez votre message..."
                      rows={1}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="flex-shrink-0 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="h-4 w-4" />
                    <span>Envoyer</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Appuyez sur Entrée pour envoyer, Maj+Entrée pour un retour à la ligne
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-500">
                <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium">Sélectionnez une conversation</p>
                <p className="text-sm">Choisissez une conversation dans la liste pour commencer</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;

